// ISDP CUSTOM: headless recorder for the DELICATE Home personalization. Renders
// nothing — it just watches the route and records a "recently viewed" entry when
// the user lands on a docs or catalog entity page. Mounted once inside Root so it
// observes every authenticated navigation.
//
// Identity-light: writes go to the recentActivity localStorage store (per-browser,
// no server, no identity). Entirely additive — if storage is unavailable the
// pushRecent calls are no-ops and Home stays as it is today.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { pushRecent, type RecentType } from './recentActivity';

// Matches a TechDocs reader or catalog entity page:
//   /docs/:namespace/:kind/:name(/optional/sub/path)
//   /catalog/:namespace/:kind/:name(/optional/tab)
// Capture groups: 1 = surface (docs|catalog), 2 = name segment.
const ENTITY_PATH_RE = /^\/(docs|catalog)\/[^/]+\/[^/]+\/([^/]+)/;

// The reader/catalog page sets document.title asynchronously after data loads, so
// we wait briefly before reading it — falling back to a prettified path segment.
const TITLE_SETTLE_MS = 600;

// Turn a catalog name segment (e.g. "devhub-guides") into a friendly title
// ("Devhub Guides") when document.title isn't usable.
function prettifySegment(segment: string): string {
  const decoded = (() => {
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  })();
  return decoded
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

// document.title often carries a " | DevHub" / " – Backstage" style suffix — strip
// a trailing " | ..." / " – ..." / " — ..." so the strip shows just the page name.
function cleanDocumentTitle(raw: string): string {
  return raw.replace(/\s*[|–—-]\s*[^|–—-]*$/, '').trim();
}

export const RecentActivityTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const match = ENTITY_PATH_RE.exec(location.pathname);
    if (!match) return undefined;

    const surface = match[1]; // 'docs' | 'catalog'
    const nameSegment = match[2];
    const type: RecentType = surface === 'docs' ? 'doc' : 'catalog';
    // Record the entity root path, not any deep sub-page, so re-opening a doc
    // de-dupes to a single "recently viewed" tile.
    const entityRoot = match[0];

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      // Catalog entity pages set document.title to a tab-polluted string like
      // "Jenkins | Details" (name | active tab), so for catalog we use the clean,
      // stable name segment. TechDocs reader titles are the real page title, so we
      // keep document.title there (cleaned), falling back to the prettified segment.
      const fromDoc =
        type === 'doc' && typeof document !== 'undefined'
          ? cleanDocumentTitle(document.title || '')
          : '';
      let title = fromDoc || prettifySegment(nameSegment);
      // Some doc titles come through as a raw slug ("jenkins", "draeger-data-platform").
      // Prettify those; leave real titles (with spaces or mixed case like "SonarQube") alone.
      if (/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/.test(title)) title = prettifySegment(title);
      pushRecent({ type, title, url: entityRoot });
    }, TITLE_SETTLE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [location.pathname]);

  return null;
};
